import { DownloadIcon, TrashIcon } from '@/components/common/icons';
import { EXPORT_MODES, type ExportMode } from '@/types/documents.types';
import { modeLabelKey } from '@/utils/categories';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface ReviewTopBarProps {
  filename: string;
  /** "14 stran · 8 kategorií · zpracováno v paměti". */
  meta: string;
  mode: ExportMode;
  onModeChange: (mode: ExportMode) => void;
  onExport: () => void;
  isExporting: boolean;
  /** An OCR scan: the PDF download is not offered (the backend refuses it); only the text can be copied. */
  isScanned?: boolean;
  /** "Kopírovat text", which owns its request and fallback dialog. */
  copyAction: ReactNode;
  /** Throws the document away (after the route's confirmation). */
  onDiscard: () => void;
}

/**
 * The review's top bar from the prototype: file and meta, the three mode tabs, "Stáhnout: {mode}",
 * and "Kopírovat text". Page navigation and zoom live in `DocumentToolbar`, floating over the page.
 */
export const ReviewTopBar: FC<ReviewTopBarProps> = ({
  filename,
  meta,
  mode,
  onModeChange,
  onExport,
  isExporting,
  isScanned = false,
  copyAction,
  onDiscard,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[54px] flex-none flex-wrap items-center gap-x-3.5 gap-y-2.5 border-b border-line bg-white px-5 py-2">
      <div className="min-w-0 flex-[0_1_auto]">
        <div className="truncate text-[13px] font-semibold" title={filename}>
          {filename}
        </div>
        <div className="text-[10.5px] text-subtle">{meta}</div>
      </div>
      <div className="h-[26px] w-px bg-control" aria-hidden="true" />
      <div
        role="radiogroup"
        aria-label={t('review.toolbar.modeLabel')}
        className="flex gap-[2px] border border-control bg-surface p-[3px]"
      >
        {EXPORT_MODES.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            onClick={() => onModeChange(value)}
            className={`cursor-pointer border-none px-3 py-[7px] text-[12.5px] font-semibold ${
              mode === value ? 'bg-primary text-white' : 'bg-transparent text-muted'
            }`}
          >
            {t(modeLabelKey(value))}
          </button>
        ))}
      </div>
      {isScanned ? (
        <button
          type="button"
          disabled={true}
          title={t('review.toolbar.downloadScannedHint')}
          className="flex cursor-not-allowed items-center gap-2 whitespace-nowrap rounded border-none bg-control px-3.5 py-[9px] text-[13px] font-semibold text-muted"
        >
          <DownloadIcon size={14} />
          {t('review.toolbar.downloadScanned')}
        </button>
      ) : (
        <button
          type="button"
          onClick={onExport}
          disabled={isExporting}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded border-none bg-primary px-3.5 py-[9px] text-[13px] font-semibold text-white hover:bg-primary-deep disabled:cursor-progress disabled:opacity-80"
        >
          <DownloadIcon size={14} />
          {t('review.toolbar.download', { mode: t(modeLabelKey(mode)) })}
        </button>
      )}
      {copyAction}
      <div className="flex-1" />
      <button
        type="button"
        onClick={onDiscard}
        aria-label={t('review.toolbar.discard')}
        title={t('review.toolbar.discard')}
        className="flex cursor-pointer items-center gap-[7px] whitespace-nowrap rounded border border-control bg-white px-3 py-2 text-[13px] font-semibold text-muted hover:border-danger hover:text-danger"
      >
        <TrashIcon size={14} />
        <span className="max-[1040px]:hidden">{t('review.toolbar.discard')}</span>
      </button>
    </div>
  );
};
